import { useNavigate } from '@remix-run/react';
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User } from '~/models/User';
import { createUser, getClient } from '~/module/auth/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: unknown | null;
  setUser: (user: User | null) => void;
  setError: (error: unknown | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);

  useEffect(() => {
    const auth = getClient();
    setLoading(true);
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const newUser = await createUser(firebaseUser);
          setUser(newUser);
        } else {
          setUser(null);
        }
        setError(null);
      } catch (e) {
        setError(e);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, setUser, setError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function useAuthGuard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return; // 認証状態が確定するまで何もしない
    if (!user) {
      navigate({
        pathname: "/login",
        search: `?redirect_to=${window.location.href}`,
      }, { replace: true });
    }
  }, [user, loading, navigate]);
}
